import { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import { Icon } from "../components/Icon.jsx";
import { DriverActionDialog } from "./DriverActionDialog.jsx";
import { DeliveryDialog } from "./DriverTaskDetail.jsx";
import { captureDriverLocation } from "./location.js";

const DEFAULT_CENTER = [-6.966, 110.416];
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = "© OpenStreetMap contributors";

function validCoordinate(point) {
  const latitude = Number(point?.latitude);
  const longitude = Number(point?.longitude);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function latLng(point) {
  return [Number(point.latitude), Number(point.longitude)];
}

function statusClass(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (new Set(["COMPLETED", "COMPLETED_WITH_ISSUE"]).has(normalized)) return "completed";
  if (normalized === "EN_ROUTE") return "active";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "failed";
  if (normalized === "DEFERRED") return "deferred";
  return "pending";
}

function statusLabel(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (normalized === "COMPLETED") return "Selesai";
  if (normalized === "COMPLETED_WITH_ISSUE") return "Selesai dengan kendala";
  if (normalized === "EN_ROUTE") return "Sedang menuju";
  if (normalized === "FAILED") return "Gagal";
  if (normalized === "CANCELLED") return "Dibatalkan";
  if (normalized === "DEFERRED") return "Ditunda";
  return "Belum dijalankan";
}

function stopIcon(stop, selected = false) {
  const tone = statusClass(stop.status);
  const content = tone === "completed" ? "✓" : stop.displaySequence || "•";

  return L.divIcon({
    className: `driver-map-marker driver-map-marker--${tone}${selected ? " is-selected" : ""}`,
    html: `<span>${content}</span>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

function formatDistance(value) {
  const meters = Number(value);
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toLocaleString("id-ID", { maximumFractionDigits: 1 })} km`;
}

function formatDuration(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `±${minutes} menit`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `±${hours} jam ${remainder} menit` : `±${hours} jam`;
}

function routeMatchesStopOrder(route, stops = []) {
  if (String(route?.status || "").toUpperCase() !== "READY" || !Array.isArray(route?.legs)) return false;
  const orderedStopKeys = stops.map((stop) => String(stop?.key || "")).filter(Boolean);
  const stopKeySet = new Set(orderedStopKeys);
  const routedStopKeys = route.legs
    .map((leg) => String(leg?.toKey || ""))
    .filter((key) => stopKeySet.has(key));
  return routedStopKeys.length === orderedStopKeys.length
    && routedStopKeys.every((key, index) => key === orderedStopKeys[index]);
}

function depotIcon() {
  return L.divIcon({
    className: "driver-map-marker driver-map-marker--depot",
    html: "<span>G</span>",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -20],
  });
}

function poiIcon(poi) {
  const labels = {
    HOTEL: "H",
    FUEL_STATION: "S",
    WORKSHOP: "B",
    REST_AREA: "R",
    OFFICE: "K",
    TRANSIT: "T",
    PARTNER: "P",
  };

  return L.divIcon({
    className: "driver-map-marker driver-map-marker--poi",
    html: `<span>${labels[poi.poiCategory] || "•"}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

function currentLocationIcon() {
  return L.divIcon({
    className: "driver-map-current-location",
    html: "<span></span>",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function geometryPositions(route) {
  const coordinates = route?.geometry?.coordinates;
  if (!Array.isArray(coordinates)) return [];

  return coordinates
    .map(([longitude, latitude]) => [Number(latitude), Number(longitude)])
    .filter(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude));
}

function FitBounds({ points, version }) {
  const map = useMap();

  useEffect(() => {
    const valid = points.filter(validCoordinate);
    if (!valid.length) return;

    map.fitBounds(L.latLngBounds(valid.map(latLng)), {
      padding: [34, 34],
      maxZoom: 15,
    });
  }, [map, points, version]);

  return null;
}

function RecenterButton({ currentLocation, points, onLocate, locating }) {
  const map = useMap();

  function fitRoute() {
    const valid = points.filter(validCoordinate);
    if (!valid.length) return;
    map.fitBounds(L.latLngBounds(valid.map(latLng)), { padding: [34, 34], maxZoom: 15 });
  }

  function locate() {
    if (currentLocation) {
      map.flyTo(latLng(currentLocation), Math.max(map.getZoom(), 15));
      return;
    }
    onLocate();
  }

  return (
    <div className="driver-map-controls">
      <button aria-label="Tampilkan seluruh rute" onClick={fitRoute} type="button">
        <Icon name="map" size={18} />
      </button>
      <button aria-label="Tampilkan posisi saya" disabled={locating} onClick={locate} type="button">
        <Icon name="location" size={18} />
      </button>
    </div>
  );
}

function mapsUrl(stop) {
  if (!validCoordinate(stop)) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${stop.latitude},${stop.longitude}`)}`;
}

export function DriverRouteMap({ payload, online = true, onClose, onShareReport = null, actionBusy = false, onStartStop, onCompleteStop, onFailStop, onMakeNext }) {
  const depot = payload?.depot || null;
  const stops = Array.isArray(payload?.stops) ? payload.stops : [];
  const pois = Array.isArray(payload?.pois) ? payload.pois : [];
  const routeOrderReady = routeMatchesStopOrder(payload?.route, stops);
  const routePositions = useMemo(() => geometryPositions(routeOrderReady ? payload?.route : null), [payload, routeOrderReady]);
  const initialSelectedStop = stops.find((stop) => String(stop.status || "").toUpperCase() === "EN_ROUTE")
    || stops.find((stop) => new Set(["PENDING", "DEFERRED"]).has(String(stop.status || "PENDING").toUpperCase()))
    || stops[0]
    || null;
  const [selectedStop, setSelectedStop] = useState(initialSelectedStop);
  const [showPois, setShowPois] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [deliveryDialog, setDeliveryDialog] = useState(null);
  const [nextChangeTarget, setNextChangeTarget] = useState(null);
  useEffect(() => {
    if (!selectedStop) return;
    const refreshed = stops.find((stop) => stop.key === selectedStop.key);
    if (refreshed) setSelectedStop(refreshed);
  }, [stops, selectedStop?.key]);

  const visibleStops = showCompleted
    ? stops
    : stops.filter((stop) => !new Set(["COMPLETED", "COMPLETED_WITH_ISSUE"]).has(String(stop.status).toUpperCase()));
  const fitPoints = [depot, ...visibleStops, ...(showPois ? pois : [])].filter(validCoordinate);
  const center = fitPoints[0] ? latLng(fitPoints[0]) : DEFAULT_CENTER;

  function locateMe() {
    if (!navigator.geolocation) {
      setLocationError("Perangkat tidak mendukung lokasi.");
      return;
    }

    setLocating(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocating(false);
      },
      () => {
        setLocationError("Lokasi tidak dapat diakses. Periksa izin lokasi perangkat.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  }

  async function runWithLocation(callback) {
    const result = await captureDriverLocation();
    if (result.warning) setLocationError(result.warning);
    try {
      await callback(result.location, result.warning);
    } catch {
    }
  }

  const phase = payload?.execution?.phase || payload?.fleetOrder?.operationalPhase || "PREPARATION";
  const fleetOrderId = payload?.fleetOrder?.id || payload?.task?.id || payload?.fleetOrderId || null;
  const selectedStatus = String(selectedStop?.status || "PENDING").toUpperCase();
  const activeStop = stops.find((stop) => String(stop.status || "").toUpperCase() === "EN_ROUTE") || null;
  const nextPendingStop = stops.find((stop) => new Set(["PENDING", "DEFERRED"]).has(String(stop.status || "PENDING").toUpperCase())) || null;
  const selectedIsNext = Boolean(selectedStop?.key && nextPendingStop?.key === selectedStop.key);
  const selectedBlockedByActiveStop = Boolean(activeStop?.key && selectedStop?.key !== activeStop.key);
  const morningReportBlocked = Boolean(payload?.deliveryReporting?.morningRequired && !activeStop);
  const selectedMapsUrl = selectedBlockedByActiveStop || morningReportBlocked ? null : mapsUrl(selectedStop);
  const canStartSelected = payload?.capabilities?.canExecuteStops === true
    && phase === "IN_PROGRESS"
    && new Set(["PENDING", "DEFERRED"]).has(selectedStatus)
    && !activeStop
    && selectedIsNext;
  const canMakeSelectedNext = payload?.capabilities?.canExecuteStops === true
    && phase === "IN_PROGRESS"
    && new Set(["PENDING", "DEFERRED"]).has(selectedStatus)
    && !activeStop
    && !selectedIsNext;
  const selectedActive = selectedStatus === "EN_ROUTE";
  const completedCount = stops.filter((stop) => new Set(["COMPLETED", "COMPLETED_WITH_ISSUE"]).has(String(stop.status).toUpperCase())).length;
  const remainingCount = stops.filter((stop) => !new Set(["COMPLETED", "COMPLETED_WITH_ISSUE", "FAILED", "CANCELLED"]).has(String(stop.status || "PENDING").toUpperCase())).length;
  const selectedLeg = routeOrderReady
    ? payload?.route?.legs?.find((leg) => String(leg?.toKey || "") === String(selectedStop?.key || "")) || null
    : null;
  const selectedMetric = [formatDistance(selectedLeg?.distanceM), formatDuration(selectedLeg?.durationS)].filter(Boolean).join(" · ");
  const completedGeometryCount = routePositions.length > 1
    ? Math.max(1, Math.round((completedCount / Math.max(stops.length, 1)) * (routePositions.length - 1)) + 1)
    : 0;
  const completedPositions = routePositions.slice(0, completedGeometryCount);

  return (
    <div className="driver-map-page">
      <header className="driver-map-page__header">
        <button aria-label="Kembali ke detail tugas" onClick={onClose} type="button">
          <Icon name="chevronLeft" size={22} />
        </button>
        <div>
          <strong>Peta Rute</strong>
          <small>{payload?.fleetOrder?.foNumber}</small>
        </div>
        <div className="driver-map-page__tools">
          {onShareReport && (
            <button aria-label="Bagikan laporan perjalanan" className="driver-map-share-button" onClick={onShareReport} type="button">
              <Icon name="upload" size={20} />
            </button>
          )}
          <div className="driver-map-page__status">
            <span>{completedCount} selesai · {remainingCount} tersisa</span>
            <small className={online ? "is-online" : "is-offline"}>{online ? "Online" : "Offline"}</small>
          </div>
        </div>
      </header>

      <div className="driver-map-legend" aria-label="Legenda status titik">
        <span><i className="is-active" /> Sedang dituju</span>
        <span><i className="is-pending" /> Belum dikunjungi</span>
        <span><i className="is-completed" /> Selesai</span>
        <span><i className="is-poi" /> Tempat penting</span>
      </div>

      <div className="driver-map-layer-toggles">
        <button aria-pressed={showPois} className={showPois ? "is-active" : ""} onClick={() => setShowPois((value) => !value)} type="button">
          {showPois && <Icon name="check" size={14} />} Tempat penting
        </button>
        <button aria-pressed={showCompleted} className={showCompleted ? "is-active" : ""} onClick={() => setShowCompleted((value) => !value)} type="button">
          {showCompleted && <Icon name="check" size={14} />} Titik selesai
        </button>
      </div>

      {!routeOrderReady && payload?.route && (
        <div className="driver-map-route-warning" role="status">
          <Icon name="warning" size={16} /> Jalur dan perkiraan belum mengikuti urutan terbaru. Muat ulang peta saat online.
        </div>
      )}

      <div className="driver-map-canvas">
        <MapContainer center={center} zoom={11} scrollWheelZoom>
          <TileLayer attribution={ATTRIBUTION} url={TILE_URL} />
          <FitBounds points={fitPoints} version={`${showPois}:${showCompleted}`} />
          <RecenterButton
            currentLocation={currentLocation}
            locating={locating}
            onLocate={locateMe}
            points={[depot, ...visibleStops]}
          />

          {routePositions.length > 1 && (
            <>
              <Polyline pathOptions={{ color: "#91a7aa", dashArray: "8 7", opacity: 0.72, weight: 5 }} positions={routePositions} />
              {completedPositions.length > 1 && <Polyline pathOptions={{ color: "#16806f", opacity: 0.94, weight: 6 }} positions={completedPositions} />}
            </>
          )}

          {validCoordinate(depot) && (
            <Marker icon={depotIcon()} position={latLng(depot)}>
              <Popup>
                <strong>{depot.depotName || "Gudang"}</strong>
                <p>{depot.addressText || "Titik awal dan kembali"}</p>
              </Popup>
            </Marker>
          )}

          {visibleStops.filter(validCoordinate).map((stop) => (
            <Marker
              eventHandlers={{ click: () => setSelectedStop(stop) }}
              icon={stopIcon(stop, selectedStop?.key === stop.key)}
              key={stop.key}
              position={latLng(stop)}
            />
          ))}

          {showPois && pois.filter(validCoordinate).map((poi) => (
            <Marker icon={poiIcon(poi)} key={`poi:${poi.id}`} position={latLng(poi)}>
              <Popup>
                <strong>{poi.poiName}</strong>
                <p>{poi.poiCategoryLabel || "POI"}</p>
                {poi.notes && <small>{poi.notes}</small>}
              </Popup>
            </Marker>
          ))}

          {currentLocation && validCoordinate(currentLocation) && (
            <Marker icon={currentLocationIcon()} position={latLng(currentLocation)}>
              <Popup>Posisi Anda saat ini</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {locationError && <div className="driver-map-location-error">{locationError}</div>}

      {selectedStop && (
        <section className="driver-map-stop-sheet">
          <div>
            <span className={`driver-status-chip is-${statusClass(selectedStop.status) === "completed" ? "success" : "neutral"}`}>
              {statusLabel(selectedStop.status)}
            </span>
            <strong>{selectedStop.displaySequence}. {selectedStop.customerName}</strong>
            <small>{selectedStop.resolvedArea || selectedStop.addressAlias || "Area belum tersedia"}</small>
            {selectedMetric && <span className="driver-map-stop-sheet__metric"><Icon name="clock" size={15} /> {selectedMetric} <small>Perkiraan rute</small></span>}
            {activeStop && selectedStop.key !== activeStop.key && <p>Selesaikan atau tunda {activeStop.customerName} sebelum mengubah tujuan.</p>}
            {morningReportBlocked && <p>Kirim atau teruskan laporan pagi dari Detail Fleet Order sebelum menuju titik pertama.</p>}
          </div>
          {(canStartSelected || canMakeSelectedNext || selectedActive || selectedMapsUrl) && (
            <div className={`driver-map-stop-sheet__actions ${canStartSelected || canMakeSelectedNext ? "is-ready" : selectedActive ? "is-active" : "is-readonly"} ${selectedActive ? "has-paired-secondary" : "has-single-secondary"}`.trim()}>
              {canStartSelected && (
              <button className="driver-primary-button driver-map-primary-action" disabled={actionBusy} onClick={() => runWithLocation((location, warning) => onStartStop(selectedStop.key, location, warning))} type="button">
                <Icon name="route" size={17} /> Mulai menuju lokasi
              </button>
            )}
            {canMakeSelectedNext && (
              <button className="driver-primary-button driver-map-primary-action" disabled={actionBusy} onClick={() => setNextChangeTarget(selectedStop)} type="button">
                <Icon name="route" size={17} /> Jadikan tujuan berikutnya
              </button>
            )}
            {selectedActive && (
              <button className="driver-primary-button driver-map-primary-action" disabled={actionBusy} onClick={() => setDeliveryDialog({ mode: "complete", stop: selectedStop })} type="button">
                <Icon name="check" size={17} /> Selesaikan pengiriman
              </button>
            )}
              {selectedMapsUrl && (
                <a
                  className="driver-secondary-button"
                  href={selectedMapsUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Icon name="map" size={17} /> Navigasi Google Maps
                </a>
              )}
              {selectedActive && (
                <button className="driver-danger-button" disabled={actionBusy} onClick={() => setDeliveryDialog({ mode: "fail", stop: selectedStop })} type="button">Gagal atau tunda</button>
              )}
            </div>
          )}
        </section>
      )}

      {nextChangeTarget && (
        <DriverActionDialog
          actions={(
            <>
              <button className="driver-secondary-button" disabled={actionBusy} onClick={() => setNextChangeTarget(null)} type="button">Batal</button>
              <button
                className="driver-primary-button"
                disabled={actionBusy}
                onClick={() => Promise.resolve(onMakeNext(nextChangeTarget.key)).then(() => setNextChangeTarget(null)).catch(() => {})}
                type="button"
              >
                Ubah tujuan
              </button>
            </>
          )}
          description={`${nextPendingStop?.customerName || "Tujuan saat ini"} akan digantikan sebagai tujuan berikutnya.`}
          onClose={() => setNextChangeTarget(null)}
          title="Ubah tujuan berikutnya?"
        >
          <div className="driver-route-change-confirmation">
            <span className="driver-eyebrow">Tujuan baru</span>
            <strong>{nextChangeTarget.customerName}</strong>
            <p>Urutan titik tersisa akan diperbarui. Titik yang sudah selesai dan titik aktif tidak berubah.</p>
          </div>
        </DriverActionDialog>
      )}

      {deliveryDialog && (
        <DeliveryDialog
          busy={actionBusy}
          fleetOrderId={fleetOrderId}
          mode={deliveryDialog.mode}
          onClose={() => setDeliveryDialog(null)}
          onSubmit={(formPayload) => runWithLocation(async (location, warning) => {
            if (deliveryDialog.mode === "complete") {
              await onCompleteStop(deliveryDialog.stop.key, { ...formPayload, ...location }, warning);
            } else {
              await onFailStop(deliveryDialog.stop.key, { ...formPayload, ...location }, warning);
            }
            setDeliveryDialog(null);
          })}
          stop={deliveryDialog.stop}
        />
      )}
    </div>
  );
}
